import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FilterType } from '@/components/Filter'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from '@/components/Filter/ClientFields/SelectClientFilter'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import {
  ClientScreeningCasesDto,
  ClientScreeningCasesFilterKey,
} from 'api/Clients/Types/ClientScreeningCasesDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { isArchivedWorldCheckScreeningStatus } from 'api/WorldCheck/Types/ScreeningResultsDto'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { MultiCompanySelectFilter } from 'components/Filter/common.filter'
import { WorldCheckStatusFilter } from 'components/Filter/ComplianceFields/WorldCheckStatusFilter'
import { FilterCell } from 'components/Filter/FilterCell'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { getComplianceWorldCheckTypesAction } from 'store/compliance/compliance.actions'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  setGridFilters,
} from 'store/grid'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { WorldCheckApiClient } from 'utils/api/worldCheckApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { SelectClientEvent, SelectClientTable } from '../../Records/Clients/SelectClientTable'
import { worldCheckCasesColumns } from './columns'

const useMapStateToProps = () => ({
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  worldCheckTypes: useAppSelector((state) => state.compliance.worldCheckTypes),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const worldCheckApiClient = useApiClient(WorldCheckApiClient)

  return {
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchComplianceWorldCheckTypes: async () =>
      dispatch(getComplianceWorldCheckTypesAction(worldCheckApiClient)),
  }
}

const WorldCheckCasesPage: React.FC = () => {
  const worldCheckApiClient = useApiClient(WorldCheckApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const crumbs = [{ title: 'World-Check Case Status', link: links.complianceWorldCheckCases.url }]

  const [screeningCases, setScreeningCases] = useState<ClientScreeningCasesDto[]>([])
  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [clientModalFilterId, setClientModalFilterId] = useState<string>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { worldCheckTypes, gridFilterByFilterIdSelector } = useMapStateToProps()
  const { fetchComplianceWorldCheckTypes } = useMapDispatchToProps()

  const [wordCheckArchiveModalState, setWordArchiveModalState] = useState<{
    visible: boolean
    worldCheckRecordId: string
  }>({
    visible: false,
    worldCheckRecordId: '',
  })

  const permissions = auth?.permissions || []

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<ClientScreeningCasesDto[]>> => {
          const searchQuery = getSearchQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await clientsApiClient.getClientScreeningCases({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.take,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(worldCheckTypes.screeningStatuses)) await fetchComplianceWorldCheckTypes()

          if (response?.items) {
            setScreeningCases(response.items)
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<ClientScreeningCasesDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState(getPaginationInitialState(), gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const searchQuery = query?.search ?? getSearchQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await clientsApiClient.getClientScreeningCases({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setScreeningCases(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [
      clientsApiClient,
      getSearchQuery,
      getSort,
      gridActions,
      gridState.pageIndex,
      gridState.pageSize,
      gridState.sort,
      tableData,
    ]
  )

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await clientsApiClient.getClientScreeningCases({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setScreeningCases(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }
  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await clientsApiClient.getClientScreeningCases({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setScreeningCases(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    gridStatusActions.setFetching(true)
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await clientsApiClient.getClientScreeningCases({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setScreeningCases(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await clientsApiClient.getClientScreeningCases({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setScreeningCases(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleWordCheckArchiveProceed = async () => {
    try {
      const searchQuery = getSearchQuery(gridState.filter)

      await worldCheckApiClient.archiveWorldCheckRecords(
        wordCheckArchiveModalState.worldCheckRecordId
      )
      setWordArchiveModalState({
        visible: false,
        worldCheckRecordId: '',
      })
      await filterFetch({ search: searchQuery })
      setToast(successToast('Case has been successfully archived'))
    } catch (error) {
      console.error('Error archiving world check record', error)
      setWordArchiveModalState({
        visible: false,
        worldCheckRecordId: '',
      })
    }
  }

  const handleWordCheckArchiveModalOpen = async (worldCheckRecordId: string) => {
    setWordArchiveModalState({
      visible: true,
      worldCheckRecordId,
    })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {wordCheckArchiveModalState.visible && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleWordCheckArchiveProceed}
          onCancel={() =>
            setWordArchiveModalState({
              visible: false,
              worldCheckRecordId: '',
            })
          }
          visible={wordCheckArchiveModalState.visible}
          renderTitle={<div>Are you sure you want to archive this WordCheck Case?</div>}
        />
      )}
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.WorldCheckCaseStatusOverview}
        filterFields={[
          {
            name: ClientScreeningCasesFilterKey.client,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: ClientScreeningCasesFilterKey.status,
            label: 'Status',
            filter: WorldCheckStatusFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: ClientScreeningCasesFilterKey.tickmillCompany,
            label: 'Tickmill Company',
            filter: MultiCompanySelectFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
        ]}
        containerClassName='wallet-table__filter'
        getQuery={getSearchQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={screeningCases} columns={worldCheckCasesColumns}>
          <Grid
            {...gridState}
            resizable
            data={screeningCases}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
          >
            {worldCheckCasesColumns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
            <Column
              width={50}
              title=''
              filterCell={FilterCell}
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Archive',
                        'data-test': 'archive-button',
                        disabled:
                          !hasPermission(permissions, PermissionsEnum.CanUseWorldCheckScreening) ||
                          isArchivedWorldCheckScreeningStatus(gridCellProps.dataItem.status.id),
                        onClick: () => handleWordCheckArchiveModalOpen(gridCellProps.dataItem.id),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

export default WorldCheckCasesPage
